import { safeConvertToUUID } from 'uuid-encoding';
import type { Actor } from './Actor';
import type { AssignableResource, Resource } from './Resource';

export type Predicate<TResource extends Resource> = (
  actor: Actor,
  resource: TResource
) => boolean;

// UUID and base62 forms of the same id are both in circulation.
const isSameId = (left?: string, right?: string): boolean =>
  !!left && !!right && safeConvertToUUID(left) === safeConvertToUUID(right);

export const isSuperUser: Predicate<Resource> = actor => actor.isSuperUser;

export const hasAnyPermission =
  (keys: string[]): Predicate<Resource> =>
  (actor, resource) =>
    actor.hasPermission(keys, safeConvertToUUID(resource.channelId));

export const isOwner: Predicate<AssignableResource> = (actor, resource) =>
  isSameId(actor.userId, resource.ownerId);

const isAssignee = (actor: Actor, resource: AssignableResource): boolean =>
  isSameId(actor.userId, resource.assigneeId);

const isOnAssignedTeam = (
  actor: Actor,
  resource: AssignableResource
): boolean => {
  const heldRoleIds = actor.groupRoleIds(safeConvertToUUID(resource.channelId));

  return resource.assigneeGroupIds.some(teamId =>
    heldRoleIds.some(roleId => isSameId(roleId, teamId))
  );
};

export const isAssigneeOrTeamMember: Predicate<AssignableResource> = (
  actor,
  resource
) => isAssignee(actor, resource) || isOnAssignedTeam(actor, resource);
